import { useState, type ReactNode } from "react";
import {
  ArrowRightIcon,
  ChevronDownIcon,
  ExternalLinkIcon,
  MinusIcon,
} from "lucide-react";

import { CandidacySourceThread } from "./CandidacySourceThread";
import { CitationSourceIconDisplay } from "./CitationSourceIcons";
import type {
  PaperTrailCitationChange,
  PaperTrailCitationClaim,
  PaperTrailCitationSource,
} from "./paperTrailCitations";

import { Column, Row, Text } from "@/components";
import { Button } from "@/components/ui/button";
import { useUserSheet } from "@/components/user-sheet/userSheetContext";

import { cn } from "@/lib/utils";

/**
 * A candidate-sheet field row with an expandable source timeline. Every
 * paper-trail citation behind the field's value, newest first, each linking to
 * the artifact it quotes.
 */
export const CandidacySourceCitations = ({
  action,
  children,
  labelContent,
  onOpenSheetTab,
  sources,
}: {
  action?: ReactNode;
  children: ReactNode;
  labelContent: ReactNode;
  // Coarse navigation for sources living on a sheet tab (Records, Notes) —
  // switches the tab. Scroll-to-quote comes later.
  onOpenSheetTab?: (tab: "notes" | "records") => void;
  sources: PaperTrailCitationSource[];
}) => {
  /* Context */
  const { openCommunications } = useUserSheet();

  /* React Hooks */
  const [isExpanded, setIsExpanded] = useState(false);

  /* Helpers */
  // Server order is newest-conversation-first already. Sort defensively so a
  // future caller can't silently break the timeline.
  const newestFirstSources = [...sources].sort(
    (firstSource, secondSource) => secondSource.sortKey - firstSource.sortKey,
  );
  const latestSource = newestFirstSources.at(0);
  // A fresh trail (newest source within 24h) gets a green dot on the toggle,
  // so recently-learned facts stand out without expanding anything.
  const FRESH_SOURCE_WINDOW_MS = 24 * 60 * 60 * 1000;
  const hasFreshSource =
    latestSource != null &&
    Date.now() - latestSource.sortKey < FRESH_SOURCE_WINDOW_MS;
  const getIsSourceNavigable = ({ destination }: PaperTrailCitationSource) =>
    destination?.type === "comms" ||
    destination?.type === "candidacy" ||
    (destination?.type === "sheetTab" && onOpenSheetTab != null);

  /* Handlers */
  const handleToggleSources = () => {
    setIsExpanded((currentValue) => !currentValue);
  };

  const handleOpenSource = ({ destination }: PaperTrailCitationSource) => {
    if (!destination) return;

    if (destination.type === "comms") {
      // Scope rides along with the tab: the panel jumps an unscoped open to the
      // Candidate rail, which would otherwise overwrite a separate set call.
      openCommunications(destination.tab, destination.scope);
      return;
    }

    // Another candidacy's sheet. New tab on purpose: the staffer is mid-review
    // here, and navigating in place would lose their pipeline position.
    if (destination.type === "candidacy") {
      const { candidacyId, companyId, jobId, sheetTab } = destination;
      const sheetTabParam = sheetTab ? `&st=${sheetTab}` : "";
      window.open(
        `/companies/${companyId}/jobs/${jobId}?c=${candidacyId}${sheetTabParam}`,
        "_blank",
        "noopener,noreferrer",
      );
      return;
    }

    onOpenSheetTab?.(destination.tab);
  };

  /* Render */
  // Marks a click that leaves for another candidacy's sheet in a new tab. Shown
  // persistently rather than on hover: it is a property of where the citation
  // lives, not a hover affordance, and it sits beside the matching tag.
  const renderExternalLinkIcon = (source: PaperTrailCitationSource) => {
    if (source.destination?.type !== "candidacy") return null;
    return <ExternalLinkIcon className="h-4 w-4 shrink-0 text-slate-400" />;
  };

  // Flags a citation that lives outside the sheet being viewed. It appears on
  // navigable rows too — an other-candidacy citation opens in a new tab, and the
  // tag is what warns the staffer they are leaving this candidacy.
  const renderSourceTag = (source: PaperTrailCitationSource) => {
    if (!source.tag) return null;

    return (
      <Row
        className="shrink-0 rounded bg-slate-200/60 px-1.5 py-0.5"
        title={source.tagReason}
      >
        <Text className="font-plex-mono text-[10px] font-medium uppercase tracking-wider text-slate-500">
          {source.tag}
        </Text>
      </Row>
    );
  };

  // Under a source's label, one block per thing the artifact said: what the
  // candidate said, then what it wrote. An edit has no quote, so it shows only
  // the value.
  // An application answer cites a form field, so it shows the pair rather than a
  // bare quote. Everything else quotes the utterance.
  const renderClaimEvidence = (claim: PaperTrailCitationClaim) => {
    if (claim.questionAnswer) {
      return (
        <Column className="gap-1">
          <Row className="items-baseline gap-1">
            <Text className="w-5 shrink-0 font-plex-mono text-sm font-semibold text-slate-400">
              Q:
            </Text>
            <Text className="text-sm font-medium leading-relaxed text-slate-700">
              {claim.questionAnswer.question}
            </Text>
          </Row>
          <Row className="items-baseline gap-1">
            <Text className="w-5 shrink-0 font-plex-mono text-sm font-semibold text-slate-400">
              A:
            </Text>
            <Text className="text-sm font-normal leading-relaxed text-slate-600">
              {claim.questionAnswer.answer}
            </Text>
          </Row>
        </Column>
      );
    }

    if (!claim.quote) return null;
    return (
      <Text className="text-sm font-normal italic leading-relaxed text-slate-600">
        &quot;{claim.quote}&quot;
      </Text>
    );
  };

  // What the artifact wrote, as a badge per field. An edit carries no label —
  // its source label already names the field — so it shows the bare value.
  const renderChanges = (changes: PaperTrailCitationChange[]) => {
    return (
      <Row className="min-w-0 flex-wrap items-center gap-1.5">
        {changes.map((change) => (
          <Row
            className="items-center gap-1 rounded border border-slate-200 bg-white px-1.5 py-0.5"
            key={`${change.label ?? ""}-${change.value}`}
          >
            {change.label ? (
              <>
                <Text className="text-sm font-normal text-slate-500">
                  {change.label}
                </Text>
                <ArrowRightIcon className="!size-3 shrink-0 text-slate-400" />
              </>
            ) : null}
            <Text className="text-sm font-medium text-slate-700">
              {change.value}
            </Text>
          </Row>
        ))}
      </Row>
    );
  };

  const renderSourceDetail = (source: PaperTrailCitationSource) => {
    return source.claims.map((claim) => {
      const hasContent =
        claim.quote ?? claim.changes ?? claim.questionAnswer ?? claim.thread;
      if (!hasContent) return null;

      return (
        <Column className="gap-0.5 pt-1 first:pt-0" key={claim.id}>
          {renderClaimEvidence(claim)}
          {claim.changes ? renderChanges(claim.changes) : null}
          {claim.thread ? (
            <CandidacySourceThread
              internalLabel={source.internalLabel}
              turns={claim.thread}
            />
          ) : null}
        </Column>
      );
    });
  };

  const renderSources = () => {
    if (!isExpanded) return null;

    return (
      <Column className="col-span-2 mt-3 w-full gap-0 rounded-md border border-slate-200/70 bg-slate-50 pb-3 pr-5 pt-2">
        <Row className="items-center justify-between px-3 pb-3 pt-1">
          <Row className="items-center gap-2">
            <Text className="font-plex-mono text-[10px] font-medium uppercase tracking-wider text-slate-500">
              Source
            </Text>
            <Text className="inline-flex h-5 items-center rounded-full bg-slate-200 px-1.5 text-[10px] font-medium leading-none text-slate-600">
              {newestFirstSources.length}
            </Text>
          </Row>
          <Button
            className="-mr-4 h-7 w-7 p-0 text-slate-400 hover:bg-slate-200/70 hover:text-slate-600"
            onClick={handleToggleSources}
            size="icon"
            title="Minimize sources"
            type="button"
            variant="ghost"
          >
            <MinusIcon className="h-4 w-4" strokeWidth={1.5} />
          </Button>
        </Row>
        <Column className="relative ml-6 gap-0">
          {newestFirstSources.map((source, sourceIndex) => (
            <Row
              className="relative items-start gap-3 py-2.5 first:pt-1"
              key={source.id}
            >
              {sourceIndex < newestFirstSources.length - 1 ? (
                <span
                  aria-hidden
                  className="absolute -bottom-5 left-2 top-3.5 border-l border-slate-200/70"
                />
              ) : null}
              {/* Positioned (not z-raised) so it paints over the connector
                  line without also clearing the sticky tab bar on scroll. */}
              <Row className="relative mt-0.5 bg-slate-50">
                <CitationSourceIconDisplay Icon={source.Icon} />
              </Row>
              {getIsSourceNavigable(source) ? (
                <Button
                  className="h-auto min-w-0 flex-1 justify-start whitespace-normal p-0 text-left hover:bg-transparent"
                  onClick={() => {
                    handleOpenSource(source);
                  }}
                  type="button"
                  variant="ghost"
                >
                  <Column className="w-full min-w-0 gap-0.5">
                    <Row className="min-w-0 items-center gap-2">
                      <Text className="text-sm font-normal text-slate-500 hover:underline">
                        {source.label}
                      </Text>
                      {renderExternalLinkIcon(source)}
                      {renderSourceTag(source)}
                    </Row>
                    {renderSourceDetail(source)}
                  </Column>
                </Button>
              ) : (
                // No destination hosts this artifact. Provenance events (edits,
                // resume attribution) may carry no quote at all. Not a button:
                // there is no action, so nothing should announce one.
                <Column className="min-w-0 flex-1 gap-0.5">
                  <Row className="items-center gap-2">
                    <Text className="text-sm font-normal text-slate-500">
                      {source.label}
                    </Text>
                    {renderSourceTag(source)}
                  </Row>
                  {renderSourceDetail(source)}
                </Column>
              )}
            </Row>
          ))}
        </Column>
      </Column>
    );
  };

  const render = () => {
    return (
      <div className="group/evaluation col-span-2 grid grid-cols-subgrid items-start gap-x-3 py-1">
        {labelContent}
        <Row className="min-h-5 min-w-0 items-start justify-between gap-2 pl-3">
          <Column className="min-w-0 flex-1 gap-0.5">{children}</Column>
          <Row className="shrink-0 items-start gap-2">
            <Row className="order-1">{action}</Row>
            {latestSource ? (
              <Button
                className="group/source-toggle relative order-2 h-7 w-7 shrink-0 p-0 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                onClick={handleToggleSources}
                size="icon"
                title={
                  isExpanded
                    ? "Hide sources"
                    : `Most recent: ${latestSource.label}`
                }
                type="button"
                variant="ghost"
              >
                <Row className="group-hover/source-toggle:hidden">
                  <CitationSourceIconDisplay Icon={latestSource.Icon} />
                </Row>
                <ChevronDownIcon
                  className={cn(
                    "hidden h-4 w-4 group-hover/source-toggle:block",
                    isExpanded && "rotate-180",
                  )}
                  strokeWidth={1.5}
                />
                {/* Top-left: the agentic icons carry a sparkle at their
                    top-right, so the opposite corner is the free one. */}
                {hasFreshSource ? (
                  <span
                    aria-hidden
                    className="absolute left-0.5 top-0.5 h-1.5 w-1.5 rounded-full bg-green-500 ring-2 ring-white"
                  />
                ) : null}
              </Button>
            ) : null}
          </Row>
        </Row>
        {renderSources()}
      </div>
    );
  };

  return render();
};
